import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Code, EmptyState } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/**
 * Recent-attempts log for a single webhook. Surfaces status code, retry
 * attempt number, scheduled retry time, and the truncated response body.
 */
export default function DeliveriesDialog({ open, onClose, brandId, hook }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !hook?._id) return;
    setLoading(true);
    API.listBrandWebhookDeliveries({
      brand_id: brandId, webhook_id: hook._id, limit: 50,
    })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open, hook, brandId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle>Recent deliveries · {hook?.name || hook?.url || ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <EmptyState>Loading…</EmptyState>}
        {!loading && items.length === 0 && <EmptyState>No deliveries yet.</EmptyState>}
        <Stack spacing='8px'>
          {items.map((d) => (
            <Box
              key={d._id}
              sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                flexWrap='wrap'
                gap='6px'
                mb='4px'
              >
                <Stack direction='row' spacing='6px' alignItems='center' flexWrap='wrap'>
                  <Chip
                    size='small'
                    label={d.response_status || 'ERR'}
                    color={d.response_status >= 200 && d.response_status < 300 ? 'success' : 'error'}
                  />
                  <Typography variant='body2' fontWeight='600'>{d.event}</Typography>
                  {d.attempt > 1 && (
                    <Chip size='small' label={`attempt #${d.attempt}`} color='secondary' variant='outlined' />
                  )}
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  {d.duration_ms}ms · {new Date(d.createdAt).toLocaleString()}
                </Typography>
              </Stack>
              {d.next_retry_at && !d.succeeded && (
                <Typography variant='caption' sx={{ color: 'warning.main', display: 'block', mt: '2px' }}>
                  Retrying around {new Date(d.next_retry_at).toLocaleString()}
                </Typography>
              )}
              {d.error && (
                <Typography variant='caption' color='error.main' display='block' mt='4px'>
                  {d.error}
                </Typography>
              )}
              {d.response_body && (
                <Code
                  sx={{
                    display: 'block',
                    p: '6px',
                    borderRadius: '6px',
                    mt: '4px',
                    maxHeight: 80,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.75rem',
                  }}
                >
                  {d.response_body}
                </Code>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
